import React from 'react';
import { Layout, Menu, Typography, Divider, Drawer } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  SearchOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useLogout } from '../../features/auth/hooks/useLogout';

const { Sider } = Layout;
const { Title, Text } = Typography;

interface SidebarProps {
  collapsed?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

export function Sidebar({ collapsed = false, isMobile = false, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { logout } = useLogout();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
    if (onClose) onClose();
  };

  const basePath = '/app';

  const getSelectedKey = () => {
    const currentPath = location.pathname;
    if (currentPath.startsWith(`${basePath}/search`)) return `${basePath}/search`;
    return `${basePath}/search`;
  };

  const menuItems = [
    {
      key: `${basePath}/search`,
      icon: <SearchOutlined />,
      label: <Link to={`${basePath}/search`} onClick={onClose}>Поиск</Link>,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выход',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const sidebarContent = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <div
        style={{
          padding: '14px',
          textAlign: 'center',
          color: 'white',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Title level={5} style={{ color: 'white', margin: 0, fontSize: 15 }}>
          CBS Support
        </Title>
      </div>

      {/* Menu */}
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        items={menuItems}
        style={{ flex: 1, borderRight: 0 }}
      />

      {/* User info */}
      <Divider style={{ margin: '6px 0', borderColor: 'rgba(255,255,255,0.1)' }} />
      <div style={{ padding: '10px 14px', textAlign: 'center', color: 'rgba(255,255,255,0.65)' }}>
        <div style={{ marginBottom: '3px' }}>
          <UserOutlined style={{ marginRight: '6px', fontSize: 12 }} />
          <Text strong style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
            {user?.username}
          </Text>
        </div>
        <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
          {user?.role}
        </Text>
      </div>
    </div>
  );

  // Mobile: use Drawer
  if (isMobile) {
    return (
      <Drawer
        placement="left"
        onClose={onClose}
        open={!collapsed}
        closable={false}
        width={220}
        styles={{
          body: { padding: 0, background: '#001529' },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop: use fixed Sider
  return (
    <Sider
      width={200}
      theme="dark"
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
      }}
    >
      {sidebarContent}
    </Sider>
  );
}
